import { Injectable, signal, computed } from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http'
import { SuppliersService } from '../suppliers.service'
import { SuppliersRepository } from '../suppliers.repository'
import { SupplierSearchParams } from '../models/supplier-search.model'
import { Supplier } from '../models/supplier.entity'
import { Country } from '../../countries'
import { SuppliersListingPage } from '../models/supplier-listing.model'

interface SuppliersListingState {
  filters?: SupplierSearchParams
  data?: Supplier[]
  totalCount?: number
  countries?: Country[]
  error?: HttpErrorResponse
  isLoading: boolean
  isInitialized: boolean
  searchString?: string
  searchKeys?: SupplierSearchParams
  customFilters?: SupplierSearchParams
}

const SUPPLIERS_LISTING_INITIAL_STATE: SuppliersListingState = {
  isLoading: false,
  isInitialized: false,
}

@Injectable()
export class SuppliersListingStore {
  constructor(
    private suppliersService: SuppliersService,
    private suppliersRepository: SuppliersRepository,
  ) {}

  private state = signal<SuppliersListingState>(SUPPLIERS_LISTING_INITIAL_STATE)

  // Selectors

  readonly isInitialized = computed(() => this.state().isInitialized)
  readonly isLoading = computed(() => this.state().isLoading)
  readonly filters = computed(() => this.state().filters)
  readonly isFiltered = computed(() => {
    const filters = this.state().filters
    return !!filters && Object.keys(filters).length > 0
  })
  readonly isErrored = computed(() => !!this.state().error)
  readonly suppliers = computed(() => this.state().data || [])
  readonly totalCount = computed(() => this.state().totalCount || 0)
  readonly searchKeys = computed(() => this.state().searchKeys)
  readonly countries = computed(() => this.state().countries || [])

  // Effects

  async search(
    params?: SupplierSearchParams,
    returnAll?: boolean,
  ): Promise<void> {
    this.unsetError()

    return new Promise((resolve, reject) => {
      this.suppliersRepository
        .searchSuppliers$(
          this.parseStateFilterParams(this.state(), params),
          returnAll,
        )
        .subscribe({
          next: (data) => {
            resolve()
            this.setData(data)
          },
          error: (error) => {
            reject(error)
            this.setError(error)
          },
        })
    })
  }

  async delete(supplier: Supplier): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.suppliersRepository
        .alert('SUPPLIER_DELETION', supplier)
        .then((response) => {
          if (response === 'success') {
            this.setIsLoading(true)
            this.suppliersService.delete$(supplier._id).subscribe({
              next: () => {
                resolve(true)
                this.suppliersRepository.notify({
                  code: 'SUPPLIER_DELETED',
                })
              },
              error: (error: HttpErrorResponse) => {
                resolve(false)
                this.setError(error)
              },
            })
          }
        })
    })
  }

  // Reducers

  setData(data: SuppliersListingPage) {
    this.state.set({
      ...this.state(),
      data: data.data,
      totalCount: data.totalCount,
      countries: data.extData,
      isInitialized: true,
      isLoading: false,
      error: undefined,
    })
  }

  setIsLoading(isLoading: boolean) {
    this.state.set({ ...this.state(), isLoading })
  }

  setFilters(filters?: SupplierSearchParams) {
    this.state.set({
      ...this.state(),
      isInitialized: true,
      filters: { ...filters },
    })
  }

  setError(error: HttpErrorResponse) {
    this.state.set({ ...this.state(), isLoading: false, error })
  }

  unsetError() {
    this.state.set({ ...this.state(), isLoading: true, error: undefined })
  }

  addFilter(filter: SupplierSearchParams) {
    this.state.set({
      ...this.state(),
      isInitialized: true,
      filters: { ...this.state().filters, ...filter },
    })
  }

  removeFilter(filter: SupplierSearchParams) {
    const currentFilters = this.state().filters
    if (currentFilters) {
      // Create a new filters object without the specified filter properties
      const updatedFilters = { ...currentFilters }

      // Remove each property specified in the filter parameter
      for (const key of Object.keys(filter)) {
        if (key in updatedFilters) {
          delete updatedFilters[key as keyof SupplierSearchParams]
        }
      }

      this.state.set({
        ...this.state(),
        isInitialized: true,
        filters: updatedFilters,
      })
    }
  }

  setSearchKeys(searchKeys?: SupplierSearchParams) {
    this.state.set({ ...this.state(), searchKeys })
  }

  resetFilters() {
    this.state.set({
      ...this.state(),
      filters: {},
    })
  }

  private parseStateFilterParams(
    state: SuppliersListingState,
    filters?: SupplierSearchParams,
  ): SupplierSearchParams {
    let params: SupplierSearchParams = filters || {}
    if (state.searchString) {
      params.q = state.searchString
    }
    if (state.customFilters) {
      params = { ...params, ...state.customFilters }
    }
    if (state.searchKeys) {
      params = { ...params, ...state.searchKeys }
    }

    return params
  }
}
